"use client";

import React from "react";
import { Calendar, User, ExternalLink, CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function ArticleSkeleton() {
  return (
    <div className="max-w-3xl mx-auto animate-pulse">
      <div className="h-12 bg-muted rounded-md w-3/4 mb-6"></div>
      <div className="h-12 bg-muted rounded-md w-1/2 mb-8"></div>
      <div className="flex gap-4 mb-8 pb-8 border-b border-border">
        <div className="h-5 bg-muted rounded w-24"></div>
        <div className="h-5 bg-muted rounded w-32"></div>
      </div>
      <div className="space-y-4 mb-10">
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-5/6"></div>
        <div className="h-4 bg-muted rounded w-4/6"></div>
      </div>
      <div className="h-12 bg-muted rounded-md w-48"></div>
    </div>
  );
}

function ArticleConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return <ArticleSkeleton />;
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto text-center py-12 bg-destructive/10 rounded-lg border border-destructive/20">
        <CircleAlert className="w-10 h-10 text-destructive mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2 font-default">Failed to load article</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-12">
        <h2 className="text-2xl font-semibold text-foreground mb-2 font-default">Article not found</h2>
        <p className="text-muted-foreground">The article you are looking for does not exist or has been removed.</p>
      </div>
    );
  }

  const post = posts[0];
  
  
  const title = post.customFields?.article_title || post.title;
  const author = post.customFields?.author_name;
  const date = post.customFields?.publication_date;
  const excerpt = post.customFields?.article_excerpt;
  const description = post.customFields?.article_description;
  const url = post.customFields?.article_url;
  
  
  const content = excerpt || description || post.content;

  return (
    <article className="max-w-3xl mx-auto">
      <header className="mb-10">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight mb-6">
          {title}
        </h1>
        
        {(author || date) && (
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground pb-8 border-b border-border">
            {author && (
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <span className="font-medium">{author}</span>
              </div>
            )}
            {date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <time dateTime={date}>
                  {new Date(date).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
            )}
          </div>
        )}
      </header>

      {content && (
        <div className="text-lg md:text-xl leading-relaxed text-foreground/90 mb-12 whitespace-pre-wrap font-default">
          {content}
        </div>
      )}

      {url && (
        <div className="pt-4">
          <Link to={url} newTab>
            <Button size="lg" className="gap-2 text-base px-8 h-14 rounded-lg shadow-md hover:shadow-lg transition-all">
              Read on InfoWorld
              <ExternalLink className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      )}
    </article>
  );
}

export default function SingleInfoworldArticleTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "infoworld_article" }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section 
      id="single-infoworld-article-template-section" 
      className="bg-background py-16 md:py-24 lg:py-32"
    >
      <div className="container mx-auto px-4 md:px-6">
        <PostsProvider wp_query={wp_query}>
          <ArticleConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}